/**
 * Trusted Technologies & OEM Brands — premium infinite marquee.
 *
 * Uses Clearbit's public Logo API to render authentic brand marks.
 * Endpoint: https://logo.clearbit.com/{domain}?size=200
 *
 * Behaviour:
 *   - continuous right→left scroll, seamless loop (duplicated track)
 *   - CSS-driven for 60 fps smoothness (GPU-composited transform)
 *   - pauses on hover (desktop)
 *   - soft edge fades left/right via CSS mask
 */

const brands = [
  { name: "Honeywell", domain: "honeywell.com" },
  { name: "Bosch", domain: "bosch.com" },
  { name: "Axis Communications", domain: "axis.com" },
  { name: "Hikvision", domain: "hikvision.com" },
  { name: "Dahua", domain: "dahuasecurity.com" },
  { name: "Pelco", domain: "pelco.com" },
  { name: "Schneider Electric", domain: "se.com" },
  { name: "Siemens", domain: "siemens.com" },
  { name: "ABB", domain: "abb.com" },
  { name: "Legrand", domain: "legrand.com" },
  { name: "L&T", domain: "larsentoubro.com" },
  { name: "Finolex", domain: "finolex.com" },
  { name: "Polycab", domain: "polycab.com" },
  { name: "KEI Industries", domain: "kei-ind.com" },
  { name: "Cisco", domain: "cisco.com" },
  { name: "HP", domain: "hp.com" },
  { name: "Dell", domain: "dell.com" },
  { name: "Lenovo", domain: "lenovo.com" },
  { name: "APC by Schneider", domain: "apc.com" },
  { name: "D-Link", domain: "dlink.com" },
  { name: "TP-Link", domain: "tp-link.com" },
  { name: "Fluke", domain: "fluke.com" },
  { name: "Megger", domain: "megger.com" },
  { name: "Kyoritsu", domain: "kew-ltd.co.jp" },
  { name: "Hioki", domain: "hioki.com" },
  { name: "Canon", domain: "canon.com" },
  { name: "Epson", domain: "epson.com" },
  { name: "Brother", domain: "brother.com" },
  { name: "Makita", domain: "makita.com" },
  { name: "Stanley", domain: "stanleyblackanddecker.com" },
  { name: "Daikin", domain: "daikin.com" },
  { name: "CP Plus", domain: "cpplusworld.com", tag: "Make in India 🇮🇳" },
];

const logoUrl = (domain) =>
  `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;

export default function Brands() {
  const track = [...brands, ...brands]; // duplicate for seamless loop

  return (
    <section
      id="brands"
      data-testid="brands-section"
      className="relative bg-[var(--nv-paper)] text-[var(--nv-navy)] py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mb-14 md:mb-20 relative">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-7">
            <div className="overline text-[var(--nv-gold)] flex items-center gap-3">
              <span className="inline-block w-6 h-px bg-[var(--nv-gold)]" />
              <span>Trusted Technologies</span>
            </div>
            <h2 className="mt-6 display text-4xl md:text-6xl" data-testid="brands-heading">
              Trusted Technologies &amp;{" "}
              <span className="italic text-[var(--nv-gold)]">OEM brands.</span>
            </h2>
          </div>
          <p className="col-span-12 md:col-span-4 md:col-start-9 text-[15px] leading-relaxed text-[var(--nv-navy)]/70">
            We work with globally recognised manufacturers and technology partners to deliver
            reliable, standards-compliant solutions tailored to every project — across security,
            electrical, networking, automation and industrial systems.
          </p>
        </div>
      </div>

      {/* Marquee — edge-faded, hover-pause, GPU-composited */}
      <div
        className="relative"
        data-testid="brands-marquee"
        style={{
          WebkitMaskImage:
            "linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%)",
          maskImage:
            "linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%)",
        }}
      >
        <div className="brands-marquee-track flex items-center gap-14 md:gap-20 py-8">
          {track.map((b, i) => (
            <BrandItem key={`${b.name}-${i}`} brand={b} />
          ))}
        </div>
      </div>

      {/* footer note */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-10 md:mt-14">
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--nv-line)] pt-8">
          <div className="font-mono text-[10.5px] tracking-[0.24em] uppercase text-[var(--nv-navy)]/55">
            Partial listing — brands available on request
          </div>
          <div className="font-mono text-[10.5px] tracking-[0.24em] uppercase text-[var(--nv-navy)]/55">
            OEM & technology partners
          </div>
        </div>
      </div>

      {/* Local styles: marquee animation with hover pause */}
      <style>{`
        .brands-marquee-track {
          width: max-content;
          animation: brands-scroll 80s linear infinite;
          will-change: transform;
        }
        .brands-marquee-track:hover {
          animation-play-state: paused;
        }
        @keyframes brands-scroll {
          from { transform: translate3d(0, 0, 0); }
          to   { transform: translate3d(-50%, 0, 0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .brands-marquee-track { animation: none; }
        }
      `}</style>
    </section>
  );
}

function BrandItem({ brand }) {
  const handleError = (e) => {
    // Hide broken image, show elegant wordmark fallback.
    const img = e.currentTarget;
    const fallback = img.nextElementSibling;
    img.style.display = "none";
    if (fallback) fallback.style.display = "inline-flex";
  };

  return (
    <div
      className="relative shrink-0 flex flex-col items-center justify-center min-w-[130px] md:min-w-[170px]"
      data-testid={`brand-${brand.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
    >
      <div className="h-10 md:h-12 flex items-center justify-center gap-3">
        <img
          src={logoUrl(brand.domain)}
          alt={brand.name}
          width="48"
          height="48"
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={handleError}
          className="h-8 w-8 md:h-9 md:w-9 object-contain opacity-80 hover:opacity-100 transition-opacity duration-500"
        />
        <span
          style={{ display: "none" }}
          className="font-serif text-lg md:text-xl text-[var(--nv-navy)]/80 tracking-tight whitespace-nowrap"
        >
          {brand.name}
        </span>
        <span className="font-serif text-lg md:text-xl text-[var(--nv-navy)]/85 tracking-tight whitespace-nowrap">
          {brand.name}
        </span>
      </div>
      {brand.tag && (
        <span className="mt-2 font-mono text-[9px] tracking-[0.22em] uppercase text-[var(--nv-gold)] whitespace-nowrap">
          {brand.tag}
        </span>
      )}
    </div>
  );
}
